import { marked } from "marked";

// ---------- per-feature view helpers (module scope, pure) ----------
// Shared by MarkdownViewer (View More Details page) and the inline
// ClauseFeatureStrip in the claim view's Features View.

const PF_FIG_RE =
  /\b((?:Patent\s+)?Fig(?:ure)?s?\.?\s*\(?\s*\d+[A-Za-z]?(?:\s*[,/]\s*\d+[A-Za-z]?)*\)?)/gi;
export const pfEmphFig = (s: string) =>
  s.replace(PF_FIG_RE, '<span class="fig-ref">$1</span>');

const PF_STOP = new Set([
  "a", "an", "the", "and", "or", "of", "to", "with", "for", "in", "on",
  "at", "by", "said", "from", "its", "their", "that", "this", "which",
]);
// Accepts numeral lists too: "auditory canal(12, 313)" chips like "ear(10)".
const PF_PART_TERM_RE =
  /([A-Za-z][A-Za-z-]*(?:\s+[A-Za-z][A-Za-z-]*){0,2})(\s*\(\s*\d+[a-zA-Z]?(?:\s*,\s*\d+[a-zA-Z]?)*\s*\))/g;

// Same claim-part emphasis as the classic view, but seeded from the element
// clause so every sibling column boxes the same terms.
export const pfBuildEmphParts = (clause: string) => {
  // A real part numeral is all digits, or 2+ digits with a letter suffix
  // ("300A") — "(3D)" style acronyms must NOT count as part numbers.
  const isPartNumeral = (n: string) =>
    /^\d+$/.test(n) || /^\d{2,}[a-zA-Z]$/.test(n);
  const claimParts = new Set<string>();
  (
    clause.match(/\(\s*\d+[a-zA-Z]?(?:\s*,\s*\d+[a-zA-Z]?)*\s*\)/g) || []
  ).forEach((t) => {
    (t.match(/\d+[a-zA-Z]?/g) || []).forEach((n) => {
      if (isPartNumeral(n)) claimParts.add(n);
    });
  });
  return (s: string) =>
    s.replace(PF_PART_TERM_RE, (full: string, phrase: string, numPart: string) => {
      const nums: string[] = numPart.match(/\d+[a-zA-Z]?/g) || [];
      if (!nums.some((n) => claimParts.has(n))) return full;
      const words = phrase.split(/\s+/);
      const dropped: string[] = [];
      while (words.length > 1 && PF_STOP.has(words[0].toLowerCase())) {
        dropped.push(words.shift() as string);
      }
      const prefix = dropped.length ? dropped.join(" ") + " " : "";
      return `${prefix}<span class="fig-ref">${words.join(" ")}${numPart}</span>`;
    });
};

export const pfStripAccused = (md: string) =>
  (md || "").replace(/\n*####\s*Accused Instrumentality:[\s\S]*$/i, "").trimEnd();

export const pfExtractAccusedUrl = (md: string) => {
  const m = (md || "").match(
    /####\s*Accused Instrumentality:[\s\S]*?!\[[^\]]*\]\(\s*([^)\s]+)/i
  );
  return m ? m[1] : "";
};

export const pfExtractClause = (md: string) => {
  const m = pfStripAccused(md).match(
    /####\s*Feature:\s*([\s\S]*?)(?:\n\s*\||\n####|$)/i
  );
  return m ? m[1].replace(/\s+/g, " ").trim() : "";
};

// Canonical form of an element clause for sibling matching (hex codes repeat
// across elements, so the clause text is the reliable per-element signal).
export const pfNormClause = (s: string) =>
  (s || "").replace(/\s+/g, " ").trim().toLowerCase();

export const pfExtractFigureInsight = (md: string) => {
  const base = pfStripAccused(md);
  const idx = base.search(/####\s*Figure Insight:/i);
  if (idx < 0) return "";
  return base.slice(idx).replace(/^####\s*Figure Insight:\s*/i, "").trim();
};

export const pfEscapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const pfNorm = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// A 6-digit hex color or null — alpha suffixes (e.g. `${hex}14`) are appended
// to it, so shorthand/invalid values must not slip through.
export const pfHex = (h: any) =>
  typeof h === "string" && /^#[0-9A-Fa-f]{6}$/.test(h) ? h : null;

// Canonical figure key: "FIG_3A" / "Figure_3a" / "FIG. 3A" → "FIG3A", so item
// figure_numbers can be matched against the claim's legend keys.
export const pfNormFig = (s: any) =>
  String(s || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .replace(/^FIGURE/, "FIG");

// Parse the vision claim-chart markdown table into header + data rows.
export const pfParseChart = (
  md: string
): { header: string[]; rows: string[][] } | null => {
  const lines = pfStripAccused(md).split("\n");
  const start = lines.findIndex((l) => l.trim().startsWith("|"));
  if (start < 0) return null;
  const block: string[] = [];
  for (let i = start; i < lines.length && lines[i].trim().startsWith("|"); i++) {
    block.push(lines[i].trim());
  }
  if (block.length < 2) return null;
  const parseCells = (row: string) =>
    row.replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const isSeparator = (cells: string[]) =>
    cells.every((c) => /^:?-{2,}:?$/.test(c) || c === "");
  const header = parseCells(block[0]);
  const rows = block.slice(1).map(parseCells).filter((c) => !isSeparator(c));
  if (rows.length === 0) return null;
  return { header, rows };
};

// Rows of a chart belonging to one sub-feature (first-cell match). A
// single-row chart (e.g. after a single-element reanalysis) counts as own.
// The chart row's numeral list can drift from the sub_feature label (e.g.
// "second chamber(195)" vs "second chamber(165, 195, 234, 235)"), so when the
// full-string match fails, retry with the parenthetical numerals stripped.
export const pfStripNums = (s: string) => (s || "").replace(/\([^)]*\)/g, " ");
export const pfOwnChartRows = (
  chart: { header: string[]; rows: string[][] },
  subFeature: string
) => {
  const matchBy = (key: (s: string) => string) => {
    const target = key(subFeature);
    if (!target) return [];
    return chart.rows.filter((c) => {
      const first = key(c[0]);
      return !!first && (first.includes(target) || target.includes(first));
    });
  };
  let own = matchBy(pfNorm);
  if (own.length === 0) own = matchBy((s) => pfNorm(pfStripNums(s)));
  if (own.length > 0) return own;
  if (chart.rows.length === 1) return chart.rows;
  return [];
};

// The backend appends a literal "(No reference numeral found)" note to Feature
// cells it couldn't tie to a numbered part. Strip the note and instead give the
// feature text an amber dotted underline with an instant CSS tooltip on hover
// (the native title tooltip proved unreliable inside the rendered table).
export const NO_NUMERAL_NOTE_RE = /\(\s*No reference numeral found\.?\s*\)/i;
const NO_REF_STYLE_ID = "no-ref-flag-styles";
export const ensureNoRefStyles = () => {
  if (document.getElementById(NO_REF_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = NO_REF_STYLE_ID;
  el.innerHTML = `
    .no-ref-text { position: relative; border-bottom: 2px dotted #D97706; cursor: help; }
    .no-ref-text::after {
      content: "No reference numeral found";
      position: absolute; left: 0; bottom: calc(100% + 6px); z-index: 50;
      background: #1f2937; color: #fff; font-size: 11px; line-height: 1.3;
      padding: 5px 8px; border-radius: 6px; white-space: nowrap;
      opacity: 0; pointer-events: none; transform: translateY(2px);
      transition: opacity .12s ease, transform .12s ease;
    }
    .no-ref-text:hover::after { opacity: 1; transform: translateY(0); }
  `;
  document.head.appendChild(el);
};
export const wrapNoRef = (inner: string) => {
  ensureNoRefStyles();
  return `<span class="no-ref-text">${inner}</span>`;
};
// Markdown-level pass for the main chart: in table rows, drop the note from any
// cell that carries it and wrap that cell's text in the tooltip span.
export const flagNoNumeralMd = (md: string) =>
  md
    .split("\n")
    .map((line) => {
      const t = line.trim();
      if (!t.startsWith("|") || !NO_NUMERAL_NOTE_RE.test(t)) return line;
      const cells = t.replace(/^\|/, "").replace(/\|$/, "").split("|");
      const out = cells.map((c) =>
        NO_NUMERAL_NOTE_RE.test(c)
          ? ` ${wrapNoRef(c.replace(NO_NUMERAL_NOTE_RE, "").trim())} `
          : c
      );
      return "|" + out.join("|") + "|";
    })
    .join("\n");

export const pfCellHtml = (c: string, emphParts: (s: string) => string) => {
  const flagged = NO_NUMERAL_NOTE_RE.test(c);
  const clean = flagged ? c.replace(NO_NUMERAL_NOTE_RE, "").trim() : c;
  try {
    const html = marked.parseInline(pfEmphFig(emphParts(clean))) as string;
    return flagged ? wrapNoRef(html) : html;
  } catch {
    return pfEscapeHtml(clean);
  }
};

// The emphasis regex captures at most 3 words before the numeral, so long
// feature names chip only their tail ("first outer load [path support
// structure(1307, 1308)]"). When the preceding text completes a known
// sub-feature name, pull it inside the chip so the whole name is boxed.
export const pfExtendChipPrefix = (
  chip: HTMLElement,
  subFeatures: string[]
) => {
  const chipNorm = pfNorm(chip.textContent || "");
  if (!chipNorm) return;
  const cands = subFeatures
    .filter((s) => {
      const n = pfNorm(s);
      return n.length > chipNorm.length && n.endsWith(chipNorm);
    })
    .sort((a, b) => b.length - a.length);
  if (!cands.length) return;
  const prev = chip.previousSibling;
  if (!prev || prev.nodeType !== Node.TEXT_NODE) return;
  const prevText = prev.textContent || "";
  for (const s of cands) {
    const n = pfNorm(s);
    const normPrefix = n.slice(0, n.length - chipNorm.length);
    // Walk backwards through the preceding text until its normalized tail
    // equals the missing prefix — that's the raw cut point.
    let acc = "";
    let cut = -1;
    for (let i = prevText.length - 1; i >= 0; i--) {
      const ch = prevText[i].toLowerCase();
      if (/[a-z0-9]/.test(ch)) acc = ch + acc;
      if (acc === normPrefix) {
        cut = i;
        break;
      }
      if (acc.length > normPrefix.length) break;
    }
    if (cut < 0) continue;
    // Don't split mid-word ("thirst" must not match "first")
    if (cut > 0 && /[a-zA-Z0-9]/.test(prevText[cut - 1])) continue;
    prev.textContent = prevText.slice(0, cut);
    chip.insertBefore(document.createTextNode(prevText.slice(cut)), chip.firstChild);
    return;
  }
};

// ---------- claim-structure hex lookup ----------
// The claim structure (indexing) and the vijilization items assign colors
// INDEPENDENTLY and can disagree. The claim-structure hex is the reference
// (it's what the Claim Features panel and clause banners show) — these
// helpers let item-driven surfaces (pills, cards, chips) reuse it.

export interface ClaimHexEntry {
  desc: string;
  hex: string;
  nums: string[];
  name: string;
}

const numeralsIn = (s: string) => {
  const out: string[] = [];
  (s.match(/\(([^)]*)\)/g) || []).forEach((g) =>
    (g.match(/\d+[a-zA-Z]?/g) || []).forEach((n) => out.push(n.toLowerCase()))
  );
  return out;
};

export const pfClaimHexEntries = (claim: any): ClaimHexEntry[] => {
  const out: ClaimHexEntry[] = [];
  const push = (p: any) => {
    const hex = pfHex(p?.hex_code);
    const desc = String(p?.description || "");
    if (!hex || !desc.trim()) return;
    out.push({
      desc,
      hex,
      nums: numeralsIn(desc),
      name: pfNorm(desc.replace(/\([^)]*\)/g, " ")),
    });
  };
  const pre = claim?.claim_preamble || claim?.preamble;
  if (Array.isArray(pre)) pre.forEach(push);
  else if (pre) push({ description: pre, hex_code: claim?.hex_code });
  (claim?.elements || []).forEach((el: any) => {
    if (el?.features?.length) el.features.forEach(push);
    else push(el);
  });
  return out;
};

// Exact text first, then numeral-stripped name, then best numeral overlap —
// numerals LAST because sibling features often share a numeral list.
export const pfClaimHexForSub = (
  sub: any,
  entries: ClaimHexEntry[]
): string | null => {
  const s = String(sub || "");
  if (!s.trim() || entries.length === 0) return null;
  const norm = pfNorm(s);
  const exact = entries.find((e) => pfNorm(e.desc) === norm);
  if (exact) return exact.hex;
  const name = pfNorm(s.replace(/\([^)]*\)/g, " "));
  if (name) {
    const byName = entries.find((e) => e.name === name);
    if (byName) return byName.hex;
  }
  const nums = new Set(numeralsIn(s));
  if (nums.size) {
    let best: ClaimHexEntry | null = null;
    let bestHits = 0;
    entries.forEach((e) => {
      const hits = e.nums.filter((n) => nums.has(n)).length;
      if (hits > bestHits) {
        best = e;
        bestHits = hits;
      }
    });
    if (best) return (best as ClaimHexEntry).hex;
  }
  return null;
};

// Chart restricted to the item's OWN sub-feature row — full table as fallback
// if the row can't be matched (used in the expanded modal).
export const pfBuildOwnChartHtml = (
  md: string,
  subFeature: string,
  emphParts: (s: string) => string
) => {
  const chart = pfParseChart(md);
  if (!chart) return "";
  const own = pfOwnChartRows(chart, subFeature);
  const shown = own.length > 0 ? own : chart.rows;
  // Own rows show the Figure Insight in the Analysis cell (it carries the
  // citations the plain analysis text lacks). Fallback full-table rows belong
  // to other features, so those keep their original analysis.
  const insight = own.length > 0 ? pfExtractFigureInsight(md) : "";
  const analysisIdx = chart.header.findIndex((h) => /analysis/i.test(h));
  const thead = `<thead><tr>${chart.header
    .map((h) => `<th>${pfCellHtml(h, emphParts)}</th>`)
    .join("")}</tr></thead>`;
  const tbody = `<tbody>${shown
    .map(
      (r) =>
        `<tr>${r
          .map(
            (c, ci) =>
              `<td>${pfCellHtml(
                ci === analysisIdx && insight ? insight : c,
                emphParts
              )}</td>`
          )
          .join("")}</tr>`
    )
    .join("")}</tbody>`;
  return `<table>${thead}${tbody}</table>`;
};
